import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import TaskBadge from 'components/Tasks/TaskBadge';
import TaskUtils from 'services/TaskUtils';
import { DefaultDivider } from 'services/UIUtils';
import './style.scss';
import useFetchPatients from 'hooks/useFetchPatients';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import Icon from 'components/Utils/NewIcon';
import { useAppSelector } from 'careand-redux/store';
import { IsTypeNurse } from 'careand-redux/selectors';
import StaffUtils from 'services/StaffUtils';
import PatientUtils from 'services/PatientUtils';

const SORT_OPTIONS = {
  RELEVANT_DATE: 'Relevant By Date',
  DUE_DATE: 'Due Date',
};

const FILTER_OPTIONS = {
  ALL: 'All',
  MEDICAL_ORDERS: 'Medical Orders',
  TASKS: 'Tasks',
};

const AssignedUserFilter = ({ assignedUsers, assignedUserFilter, setAssignedUserFilter }) => {
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  const userOptionsWithIds = useMemo(() => {
    return assignedUsers
      .map((userId) => {
        const staff = physicians[userId] || admins[userId];
        const name = staff ? StaffUtils.getName(staff) : null;
        return name ? { id: userId, name } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [assignedUsers, physicians, admins]);

  const userOptions = useMemo(() => {
    return ['All', ...userOptionsWithIds.map((opt) => opt.name)];
  }, [userOptionsWithIds]);

  const userIdMap = useMemo(() => {
    const map = { All: 'all' };
    userOptionsWithIds.forEach((option) => {
      map[option.name] = option.id;
    });
    return map;
  }, [userOptionsWithIds]);

  const handleChange = (e) => {
    const selectedName = e.target.value;
    const userId = userIdMap[selectedName];
    setAssignedUserFilter(userId);
  };

  const currentDisplayValue = useMemo(() => {
    if (assignedUserFilter === 'all') return 'All';
    const staff = physicians[assignedUserFilter] || admins[assignedUserFilter];
    return staff ? StaffUtils.getName(staff) : 'All';
  }, [assignedUserFilter, physicians, admins]);

  return (
    <div className="filter-wrapper">
      <Icon height="20px" width="20px" src="filter" className="filter-icon blue" />
      <DefaultSelectField
        placeholder="Assigned To"
        value={currentDisplayValue}
        inputs={userOptions}
        onChange={handleChange}
      />
    </div>
  );
};

const TaskList = ({
  tasks,
  title,
  onClick,
  hideFilter = false,
  assignedUserFilter,
  setAssignedUserFilter,
  showAssignedUserFilter = false,
  headerContent,
}) => {
  const [sortBy, setSortBy] = useState(SORT_OPTIONS.DUE_DATE);
  const [sortDirection, setSortDirection] = useState('desc');
  const [filter, setFilter] = useState(FILTER_OPTIONS.ALL);
  const [searchQuery, setSearchQuery] = useState('');

  const isNurse = useAppSelector(IsTypeNurse);
  const ownId = useAppSelector((state) => state.authentication.user?.id);
  const patients = useAppSelector((state) => state.patients.byId);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);

  useFetchPatients(tasks);

  const toggleSortDirection = () => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const getPatientName = useCallback(
    (task) => {
      const patient = patients[task.patient_id];
      return patient ? PatientUtils.getName(patient, '', false) : '';
    },
    [patients],
  );

  const getAuthorName = useCallback(
    (task) => {
      const staff = physicians[task.author_id] || admins[task.author_id];
      return staff ? StaffUtils.getName(staff) : '';
    },
    [physicians, admins],
  );

  // Get unique assigned users from tasks
  const assignedUsers = useMemo(() => {
    const userIds = new Set();
    tasks.forEach((task) => {
      if (task.assigned_user_id && task.assigned_user_id !== 'none') {
        userIds.add(task.assigned_user_id);
      }
    });
    return Array.from(userIds);
  }, [tasks]);

  const sortedAndFilteredTasks = useMemo(() => {
    let filtered = tasks;

    // Apply filter
    if (filter === FILTER_OPTIONS.MEDICAL_ORDERS) {
      filtered = tasks.filter((task) => task.title === 'Medical Order');
    } else if (filter === FILTER_OPTIONS.TASKS) {
      filtered = tasks.filter((task) => task.title !== 'Medical Order');
    }

    // Apply assigned user filter
    if (showAssignedUserFilter && assignedUserFilter && assignedUserFilter !== 'all') {
      filtered = filtered.filter((task) => task.assigned_user_id === assignedUserFilter);
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((task) => {
        const titleMatch = (task.title || '').toLowerCase().includes(query);
        const detailsMatch = (task.details || '').toLowerCase().includes(query);
        const patientMatch = getPatientName(task).toLowerCase().includes(query);
        const authorMatch = getAuthorName(task).toLowerCase().includes(query);
        return titleMatch || detailsMatch || patientMatch || authorMatch;
      });
    }

    // Sort filtered tasks
    let sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case SORT_OPTIONS.DUE_DATE:
          comparison = (a.due_date || 0) - (b.due_date || 0);
          break;
        case SORT_OPTIONS.RELEVANT_DATE:
          comparison = (a.date || 0) - (b.date || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    const ordered = TaskUtils.sortTasks(sorted);

    // Bubble unfinished tasks with the same unread signal that drives the red badge
    // to the top. Gated on !task.status so completed tasks (which sortTasks already
    // parks at the bottom) don't leapfrog active unfinished work just because they
    // accrued a late comment. Array.prototype.sort is stable (ES2019+), so within
    // each bucket the user-selected sort key and finished/unfinished split are kept.
    return [...ordered].sort((a, b) => {
      const aUnread = !a.status && TaskUtils.hasUnreadSignal(a, ownId) ? 1 : 0;
      const bUnread = !b.status && TaskUtils.hasUnreadSignal(b, ownId) ? 1 : 0;
      return bUnread - aUnread;
    });
  }, [
    tasks,
    sortBy,
    sortDirection,
    filter,
    showAssignedUserFilter,
    assignedUserFilter,
    searchQuery,
    getPatientName,
    getAuthorName,
    ownId,
  ]);

  return (
    <div className="underside-right-panel task-list" key={title}>
      <Title title={title} />
      {headerContent}
      <div className="sort-controls">
        {sortedAndFilteredTasks.length > 1 && (
          <div className="sort-wrapper">
            <Icon
              height="20px"
              width="20px"
              src={`sort-${sortDirection}`}
              className="sort-icon blue"
              onClick={toggleSortDirection}
              alt={sortDirection === 'asc' ? 'Oldest first' : 'Most recent first'}
              showTooltip={true}
              tooltipPlace="top"
            />
            <DefaultSelectField
              placeholder="Sort by"
              value={sortBy}
              inputs={Object.values(SORT_OPTIONS)}
              onChange={(e) => setSortBy(e.target.value)}
              style={{ width: 'fit-content' }}
            />
            <span className="sort-direction-label" onClick={toggleSortDirection}>
              {sortDirection === 'asc' ? 'Oldest first' : 'Most recent first'}
            </span>
          </div>
        )}
        {showAssignedUserFilter && assignedUsers.length > 1 && (
          <AssignedUserFilter
            assignedUsers={assignedUsers}
            assignedUserFilter={assignedUserFilter}
            setAssignedUserFilter={setAssignedUserFilter}
          />
        )}
        {isNurse && !hideFilter && (
          <div className="filter-wrapper">
            <Icon height="20px" width="20px" src="filter" className="filter-icon blue" />
            <DefaultSelectField
              placeholder="Filter"
              value={filter}
              inputs={Object.values(FILTER_OPTIONS)}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        )}
        {tasks.length > 0 && !hideFilter && (
          <div className="search-wrapper">
            <Icon height="16px" width="16px" src="search" className="search-icon" />
            <input
              type="text"
              className="task-search-input"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <Icon
                height="14px"
                width="14px"
                src="xmark"
                className="search-clear"
                onClick={() => setSearchQuery('')}
              />
            )}
          </div>
        )}
      </div>
      <TaskElements tasks={sortedAndFilteredTasks} onClick={onClick} />
    </div>
  );
};

const TaskElements = ({ tasks, onClick }) =>
  tasks.length === 0 ? (
    <NoContent />
  ) : (
    <ul className="tasks padded-box">
      {tasks.map((task) => (
        <div key={task.id}>
          <TaskBadge task={task} onClick={onClick} />
          <DefaultDivider />
        </div>
      ))}
    </ul>
  );

const Title = React.memo(({ title }) =>
  title ? (
    <span className="small-title dashboard-section-title padded-title capitalize">{title}</span>
  ) : null,
);

const NoContent = () => <span className="padded-title">No items to show</span>;

TaskList.propTypes = {
  tasks: PropTypes.array,
  title: PropTypes.string,
};

TaskList.defaultProps = {
  tasks: [],
  title: undefined,
};

export default React.memo(TaskList);
